import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { enregistrerReseauxSociaux, fetchReseauxSociaux } from '../services/api';
import EditeurReseaux, { type LigneReseau } from './EditeurReseaux';

/**
 * Réseaux sociaux du comité, tenus par le président.
 *
 * Une association de village change de réseau au gré de ses bénévoles : la
 * page Facebook d'hier devient un compte Instagram, puis une chaîne WhatsApp.
 * La liste se tient donc ici, et non dans le code.
 *
 * La saisie est celle des sponsors, au composant près : mêmes règles, mêmes
 * réseaux, même geste.
 */
export default function ReseauxSociauxAdmin() {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ['reseaux-sociaux'],
    queryFn: fetchReseauxSociaux,
  });

  const enregistrer = useMutation({
    mutationFn: (liste: LigneReseau[]) => enregistrerReseauxSociaux(liste),
    onSuccess: () => {
      setNotice('Réseaux enregistrés.');
      setErreur(null);
      queryClient.invalidateQueries({ queryKey: ['reseaux-sociaux'] });
    },
    onError: (e: Error) => {
      setErreur(e.message);
      setNotice(null);
      // La liste affichée redevient celle du serveur : garder à l'écran un
      // état refusé laisserait croire qu'il est enregistré.
      queryClient.invalidateQueries({ queryKey: ['reseaux-sociaux'] });
    },
  });

  if (isPending) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {notice && (
        <p className="text-sm rounded-xl px-3 py-2 bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200 flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          {notice}
        </p>
      )}
      {erreur && <p className="text-sm rounded-xl px-3 py-2 bg-red-50 text-red-700">{erreur}</p>}

      <EditeurReseaux
        reseaux={(data?.reseaux ?? []).map((r: any) => ({ reseau: r.reseau, url: r.url }))}
        catalogue={data?.catalogue ?? []}
        onEnregistrer={(liste) => enregistrer.mutate(liste)}
        enCours={enregistrer.isPending}
        messageVide="Aucun réseau renseigné. Le bloc « Suivez-nous » n'apparaît pas sur la page d'accueil tant que cette liste est vide."
      />
    </div>
  );
}
